import BigNumber from 'bignumber.js'
import { AirdropBox } from 'config/types'
import useMatchBreakpoints from 'hooks/useMatchBreakpoints'
import { usePrivateSocket } from 'hooks/usePrivateSocket'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from 'state/auth/hooks'
import { getFullDisplayBalance } from 'utils/formatBalance'
import { HunnyToast } from 'utils/toastify'

export const useIncomingBoxes = () => {
  const { isSigned } = useAuth()
  const socket = usePrivateSocket()
  const { isMobile } = useMatchBreakpoints()
  const [boxCount, setBoxCount] = useState<number>(0)
  const incomingBoxesRef = useRef<(AirdropBox & { isFirstTime?: boolean })[]>([])
  const [droppingBox, setDroppingBox] = useState<AirdropBox & { isFirstTime?: boolean }>(null)

  const incomingBoxes = useMemo(() => {
    return [...incomingBoxesRef.current]
  }, [boxCount])

  useEffect(() => {
    if (boxCount && !droppingBox) {
      setDroppingBox(getNextBox())
    }
  }, [boxCount, droppingBox])

  useEffect(() => {
    if (!isSigned || !socket || isMobile) {
      return
    }

    const event = socket.on('airdrop.rewarded', (data) => {
      incomingBoxesRef.current.push({ amount: data.amount, code: data.code, isFirstTime: data.is_first })
      setBoxCount(incomingBoxesRef.current.length)
    })

    return () => {
      event.off()
    }
  }, [isSigned, socket])

  const getNextBox = useCallback(() => {
    const nextBox = incomingBoxesRef.current.pop()
    setBoxCount(incomingBoxesRef.current.length)
    return nextBox
  }, [incomingBoxes])

  const receivedBox = useCallback(() => {
    setTimeout(() => {
      setDroppingBox(null)
    }, 1000)
  }, [setDroppingBox])

  return { incomingBoxes, boxCount, droppingBox, receivedBox }
}

export const useNotificationAirdropMobile = () => {
  const { t } = useTranslation()

  const pushNotification = useCallback((airdropBoxes: AirdropBox[]) => {
    const totalAmountReceived = airdropBoxes.reduce((result, current) => result.plus(current.amount), new BigNumber(0))
    if (totalAmountReceived.isEqualTo(0)) {
      HunnyToast.showNoti(t('Oops! No HUSD here. Play for another chance!'))
      return
    }

    HunnyToast.showNoti(
      // eslint-disable-next-line no-template-curly-in-string
      t('${{value}} HUSD added to your locked balance!', {
        value: getFullDisplayBalance(totalAmountReceived, 0, 4),
      }),
    )
  }, [])

  return pushNotification
}

export const useNotificationAirdropDesktop = () => {
  const { t } = useTranslation()
  const timeoutRef = useRef(null)

  const pushNotification = useCallback((airdropBoxes: AirdropBox[]) => {
    clearTimeout(timeoutRef.current)
    const totalAmountReceived = airdropBoxes.reduce((result, current) => result.plus(current.amount), new BigNumber(0))
    const husdBonusContainer = document.getElementById('notification-husdbonus-container')
    const contentNotificationBonus = document.getElementById('notification-husdbonus')
    if (totalAmountReceived && husdBonusContainer) {
      husdBonusContainer.style.display = 'flex'
      husdBonusContainer.classList.add('appear-animation')
      /// Listen animation ended
      // eslint-disable-next-line func-names
      husdBonusContainer.addEventListener('animationend', function () {
        this.classList.remove('appear-animation')
      })
    }

    if (contentNotificationBonus && husdBonusContainer) {
      contentNotificationBonus.textContent = totalAmountReceived.isEqualTo(0)
        ? t('Oops! No HUSD here. Play for another chance!')
        : // eslint-disable-next-line no-template-curly-in-string
          t('${{value}} HUSD added to your locked balance!', {
            value: getFullDisplayBalance(totalAmountReceived, 0, 4),
          })

      timeoutRef.current = setTimeout(() => {
        husdBonusContainer.style.display = 'none'
      }, 7000)
    }
  }, [])

  return pushNotification
}
